import React, {useState} from 'react';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MenuIcon from '@mui/icons-material/Menu';
import {toast} from 'react-toastify';
import {useAuth} from 'context/AuthContext';
import {authApi} from 'utils/api';

const DRAWER_WIDTH = 200;

const styles = {
    root: {
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
    },
    appBar: {
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
    },
    toolbar: {
        justifyContent: 'space-between',
    },
    title: {
        fontWeight: 600,
        fontSize: {xs: '1rem', md: '1.5rem'},
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: {xs: 1, md: 2},
    },
    userInfo: {
        color: 'text.secondary',
        fontSize: '0.875rem',
        cursor: 'pointer',
        display: {xs: 'none', sm: 'block'},
        '&:hover': {textDecoration: 'underline'},
    },
    drawerPaper: {
        width: DRAWER_WIDTH,
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
    },
    content: {
        flexGrow: 1,
        p: {xs: 1.5, sm: 2, md: 3},
    },
};

const menuItems = [
    {label: '새 빌드 요청', path: '/', icon: <BuildIcon/>},
    {label: '빌드 히스토리', path: '/history', icon: <HistoryIcon/>},
    {label: '설정 생성기', path: '/config-generator', icon: <TuneIcon/>},
    {label: 'WAR 버전 확인', path: '/war-check', icon: <FactCheckIcon/>},
];

const adminMenuItem = {label: '관리', path: '/admin', icon: <SettingsIcon/>};

export default function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const {user, isAdmin, logout} = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [drawerOpen, setDrawerOpen] = useState(false);

    // 비밀번호 변경 다이얼로그
    const [pwOpen, setPwOpen] = useState(false);
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwSubmitting, setPwSubmitting] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleOpenPwDialog = () => {
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
        setPwOpen(true);
    };

    const handleChangePw = async (e) => {
        e.preventDefault();

        if (newPw !== confirmPw) {
            toast.error('새 비밀번호가 일치하지 않습니다.');
            return;
        }

        if (newPw.length < 4) {
            toast.error('새 비밀번호는 4자 이상이어야 합니다.');
            return;
        }

        setPwSubmitting(true);
        try {
            await authApi.changePassword(currentPw, newPw);
            toast.success('비밀번호가 변경되었습니다.');
            setPwOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.error || '비밀번호 변경 실패');
        } finally {
            setPwSubmitting(false);
        }
    };

    const allMenuItems = isAdmin ? [...menuItems, adminMenuItem] : menuItems;

    const drawerContent = (
        <List>
            {allMenuItems.map((item) => (
                <ListItemButton
                    key={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => {
                        navigate(item.path);
                        if (isMobile) setDrawerOpen(false);
                    }}
                >
                    <ListItemIcon sx={{minWidth: 36}}>
                        {item.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{fontSize: '0.875rem'}}
                    />
                </ListItemButton>
            ))}
        </List>
    );

    return (
        <Box sx={styles.root}>
            <AppBar position="fixed" sx={styles.appBar} elevation={1}>
                <Toolbar sx={styles.toolbar}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                edge="start"
                                onClick={() => setDrawerOpen(!drawerOpen)}
                            >
                                <MenuIcon/>
                            </IconButton>
                        )}
                        <Typography variant="h1" sx={styles.title}>
                            {isMobile ? '빌드 시스템' : '인투소프트 빌드 시스템'}
                        </Typography>
                    </Box>
                    <Box sx={styles.headerActions}>
                        <Typography
                            sx={styles.userInfo}
                            onClick={handleOpenPwDialog}
                            title="비밀번호 변경"
                        >
                            {user?.username} ({user?.role})
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleLogout}
                        >
                            로그아웃
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    ModalProps={{keepMounted: true}}
                    sx={{'& .MuiDrawer-paper': styles.drawerPaper}}
                >
                    <Toolbar/>
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': styles.drawerPaper,
                    }}
                >
                    <Toolbar/>
                    {drawerContent}
                </Drawer>
            )}

            <Box component="main" sx={styles.content}>
                <Toolbar/> {/* AppBar spacer */}
                <Outlet/>
            </Box>

            {/* 비밀번호 변경 다이얼로그 */}
            <Dialog open={pwOpen} onClose={() => setPwOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>비밀번호 변경</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        id="change-pw-form"
                        onSubmit={handleChangePw}
                        sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}
                    >
                        <TextField
                            label="현재 비밀번호"
                            type="password"
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="새 비밀번호"
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            helperText="4자 이상"
                        />
                        <TextField
                            label="새 비밀번호 확인"
                            type="password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            error={confirmPw !== '' && newPw !== confirmPw}
                            helperText={confirmPw !== '' && newPw !== confirmPw ? '비밀번호가 일치하지 않습니다' : ''}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPwOpen(false)}>취소</Button>
                    <Button
                        type="submit"
                        form="change-pw-form"
                        variant="contained"
                        disabled={pwSubmitting}
                    >
                        변경
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
